import { useState, useRef, useCallback, useEffect } from 'react';
import { PenLine, Type, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Signatures need to read correctly wherever they end up (onboarding PDFs,
// DQF paperwork, road test certificates) -- always a white page, regardless
// of which theme the driver was in while signing. So this canvas is
// deliberately NOT theme-reactive: solid white fill + dark ink, painted
// into the actual pixel data (not just CSS) so canvas.toDataURL() exports
// correctly. Previously the stroke was '#f5f5f5' (near-white) on a
// transparent canvas -- invisible while signing in light mode, and
// invisible once embedded in an exported white-background PDF regardless
// of theme.
const INK_COLOR = '#1a1a1a';

function fillWhite(canvas) {
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

export default function SignaturePad({ onChange, value }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const lastPos = useRef(null);

  useEffect(() => {
    if (canvasRef.current) fillWhite(canvasRef.current);
  }, [mode]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    lastPos.current = pos;
    setIsDrawing(true);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = INK_COLOR;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    onChange(canvas.toDataURL());
  }, [isDrawing, onChange]);

  const stopDraw = useCallback(() => setIsDrawing(false), []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) fillWhite(canvas);
    onChange('');
  };

  const handleTypeChange = (val) => {
    setTypedName(val);
    onChange(val ? `typed:${val}` : '');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    clearCanvas();
    setTypedName('');
    onChange('');
  };

  return (
    <div className="space-y-2">
      {/* Mode toggle */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => switchMode('draw')}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            mode === 'draw' ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <PenLine className="w-3 h-3" /> Draw
        </button>
        <button
          type="button"
          onClick={() => switchMode('type')}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
            mode === 'type' ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Type className="w-3 h-3" /> Type
        </button>
      </div>

      {mode === 'draw' ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={480}
            height={100}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            className="w-full h-[100px] rounded-lg border border-border bg-white cursor-crosshair touch-none"
            style={{ display: 'block' }}
          />
          {!value && (
            <p className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 pointer-events-none select-none">
              Sign here
            </p>
          )}
          <button
            type="button"
            onClick={clearCanvas}
            className="absolute top-2 right-2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
            title="Clear"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <input
          value={typedName}
          onChange={e => handleTypeChange(e.target.value)}
          placeholder="Type full name as signature"
          className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 italic"
          style={{ fontFamily: 'Georgia, serif' }}
        />
      )}
    </div>
  );
}